import React, { useState } from 'react';
import { LivePage, FilterBar, DataTable, Code, clustersAPI } from './LivePage';
import { useCluster } from '../context/ClusterContext';

/**
 * Cluster nodes.
 *
 * The sidebar has linked to /nodes for a while but no route was ever registered, so the
 * catch-all in App.js silently redirected it to the Dashboard — the link looked live and
 * went nowhere. This is that page.
 *
 * Source is /clusters/{id}/nodes, which is SarvKube's own node table (Postgres), kept
 * current by node sync. That is deliberately NOT the live Kubernetes node list: it carries
 * SarvKube-specific columns (label, server binding, SSH reachability) that k8s knows nothing
 * about, and it is what the node management screens already act on.
 */

// Kubernetes reports readiness as a condition; the API flattens it to a lowercase string.
const STATUS_COLOR = {
  ready: 'var(--color-success-500)',
  notready: 'var(--color-danger-500)',
  'not ready': 'var(--color-danger-500)',
  unknown: 'var(--muted)',
};

function statusColor(s) {
  return STATUS_COLOR[String(s || '').toLowerCase()] || 'var(--color-warning-500)';
}

function roleLabel(role) {
  // CONTROL_PLANE / WORKER come from the DB enum; make them read like the rest of the UI.
  const r = String(role || '').replace(/_/g, ' ').toLowerCase();
  return r ? r.charAt(0).toUpperCase() + r.slice(1) : '—';
}

export default function Nodes() {
  const { selectedCluster } = useCluster();
  const [nameFilter, setNameFilter] = useState('');

  return (
    <LivePage title="Nodes" fetchFn={id => clustersAPI.listNodes(id)}>
      {(data) => {
        const items = Array.isArray(data) ? data : (data.nodes || data.items || []);
        const q = nameFilter.trim().toLowerCase();
        const filtered = items.filter(n =>
          !q || String(n.label || n.name || '').toLowerCase().includes(q));

        return <>
          <FilterBar count={filtered.length} total={items.length} label="nodes">
            <input
              placeholder="Filter by name..."
              value={nameFilter}
              onChange={e => setNameFilter(e.target.value)}
            />
          </FilterBar>
          <DataTable
            cols={['Name', 'Role', 'Status', 'Internal IP', 'Kubelet', 'OS']}
            rows={filtered.map(n => (
              <tr key={n.id ?? n.name}>
                <td>
                  <Code>{n.label || n.name}</Code>
                  {/* Auto-discovered nodes were found by sync rather than added by hand;
                      worth showing because they have no SSH credentials of their own. */}
                  {n.auto_discovered && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--muted-2)' }}>
                      discovered
                    </span>
                  )}
                </td>
                <td style={{ fontSize: 13, color: 'var(--ink-2)' }}>{roleLabel(n.role)}</td>
                <td style={{ color: statusColor(n.status), fontWeight: 600, fontSize: 13 }}>
                  {n.status || 'unknown'}
                  {n.status_message && (
                    <span
                      title={n.status_message}
                      style={{ marginLeft: 6, color: 'var(--muted-2)', fontWeight: 400, cursor: 'help' }}
                    >ⓘ</span>
                  )}
                </td>
                <td style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                  {n.internal_ip || n.ip_address || '—'}
                </td>
                <td style={{ fontSize: 13, color: 'var(--ink-2)' }}>{n.kubelet_version || '—'}</td>
                <td style={{ fontSize: 13, color: 'var(--muted)' }}>{n.os_image || '—'}</td>
              </tr>
            ))}
          />
          {selectedCluster && (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>
              Node provisioning, SSH credentials and joining are managed per cluster under{' '}
              Manage Clusters → {selectedCluster.name}.
            </div>
          )}
        </>;
      }}
    </LivePage>
  );
}
